import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beginner-gains-app/artifacts/ycyh3qv2_goatgym_logo.png.jpeg";

const LoginPage = () => {
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    if (isRegister) {
      // Registration validations
      if (password.length < 6) {
        toast.error("La contraseña debe tener al menos 6 caracteres");
        return;
      }
      if (password !== confirmPassword) {
        toast.error("Las contraseñas no coinciden");
        return;
      }

      setLoading(true);
      const result = await register(email, password);
      setLoading(false);

      if (result.success) {
        toast.success("¡Cuenta creada! Bienvenido a GOATGYM");
        // Onboarding will be shown in HoyPage for new users
      } else {
        toast.error(result.error);
      }
    } else {
      // Login
      setLoading(true);
      const result = await login(email, password);
      setLoading(false);

      if (result.success) {
        toast.success("¡Bienvenido!");
      } else {
        toast.error(result.error);
      }
    }
  };

  const toggleMode = () => {
    setIsRegister(!isRegister);
    resetForm();
  };

  return (
    <div className="min-h-dvh bg-[#0B0B0B] flex flex-col items-center justify-center p-4 pt-safe pb-safe">
      <div className="relative z-10 w-full max-w-md animate-scale-in">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="GOATGYM - Greatest of all times" 
            className="w-32 h-32 sm:w-40 sm:h-40 object-contain mb-4"
          />
          <p className="text-[#888] text-sm sm:text-base">Tu entrenamiento. Tu nivel. Tu progreso.</p>
        </div>

        {/* Login/Register Card */}
        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-heading text-white">
              {isRegister ? "Crear Cuenta" : "Iniciar Sesión"}
            </CardTitle>
            <CardDescription className="text-[#666]">
              {isRegister ? "Regístrate para empezar" : "Ingresa tus credenciales"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#AAA] text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white placeholder:text-[#555] rounded-lg text-base"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#AAA] text-sm font-medium">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-14 bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white placeholder:text-[#555] pr-14 rounded-lg text-base"
                    data-testid="login-password-input"
                    autoComplete={isRegister ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#555] hover:text-[#D4AF37] transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {isRegister && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#AAA] text-sm font-medium">Confirmar Contraseña</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-14 bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white placeholder:text-[#555] rounded-lg text-base"
                    data-testid="register-confirm-password"
                    autoComplete="new-password"
                  />
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold uppercase tracking-wider rounded-lg text-base transition-all duration-200 active:scale-[0.98] shadow-lg shadow-[#D4AF37]/20 mt-2"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {isRegister ? "Creando cuenta..." : "Entrando..."}
                  </>
                ) : (
                  isRegister ? "Crear Cuenta" : "Entrar"
                )}
              </Button>
            </form>

            {/* Toggle between login/register */}
            <div className="mt-6 pt-6 border-t border-[#2A2A2A]">
              <p className="text-center text-sm text-[#888]">
                {isRegister ? "¿Ya tienes cuenta?" : "¿No tienes cuenta?"}
              </p>
              <button
                onClick={toggleMode}
                className="w-full mt-3 py-3 text-[#D4AF37] font-semibold text-sm hover:text-[#E5C54D] transition-colors flex items-center justify-center gap-2"
                data-testid="toggle-auth-mode"
              >
                {isRegister ? (
                  <>
                    <ArrowLeft className="w-4 h-4" />
                    Volver a Iniciar Sesión
                  </>
                ) : (
                  "Crear cuenta gratis"
                )}
              </button>
            </div>

            {/* Free tier info for register mode */}
            {isRegister && (
              <div className="mt-4 p-3 bg-[#1A1A1A] rounded-lg">
                <p className="text-[#888] text-xs text-center">
                  Plan FREE: Acceso a rutinas básicas y seguimiento de progreso.
                  <br />
                  <span className="text-[#D4AF37]">Mejora a Premium</span> para dieta personalizada y mentoría.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Version label */}
        <p className="text-center text-xs text-[#444] mt-6" data-testid="version-label">
          Version 3.0 - May 2026
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
